import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Input } from 'antd';

import { useAntMessage, usePasswordValidations } from '@/shared/lib/hooks';

import { useChangePasswordMutation } from '../../api/profile.api';
import type { ChangePassword } from '../../model/types/change-password';

import cls from './change-password-form.module.css';

type FormValues = ChangePassword & { repeatPassword: string };

interface ChangePasswordFormProps {
    viewerId: number;
}

export const ChangePasswordForm = memo((props: ChangePasswordFormProps) => {
    const { viewerId } = props;

    const { t } = useTranslation('profile-management');

    const message = useAntMessage();

    const [form] = Form.useForm<FormValues>();

    const [changePasswordMutation, { isLoading, isSuccess }] =
        useChangePasswordMutation();

    const passwordValidations = usePasswordValidations(
        t('passwordFieldNameError', { ns: 'profile-management' }),
    );

    const onChange = useCallback(
        (formValues: FormValues) => {
            const { repeatPassword, ...body } = formValues;

            changePasswordMutation({ id: viewerId, ...body });
        },
        [changePasswordMutation, viewerId],
    );

    useEffect(() => {
        if (isSuccess) {
            message.info?.({
                content: t('changePasswordSuccessMessage'),
            });

            form.resetFields();
        }
    }, [t, isSuccess, form, message]);

    return (
        <Form
            autoComplete="off"
            className={cls.root}
            form={form}
            layout="vertical"
            onFinish={onChange}
        >
            <Form.Item
                hasFeedback
                label={t('oldPasswordInputLabel')}
                name="password"
                rules={[
                    {
                        required: true,
                        message: t('requiredControlError', {
                            controlName: t('passwordFieldNameError'),
                            ns: 'translation',
                        }),
                    },
                ]}
            >
                <Input
                    className={cls.control}
                    disabled={isLoading}
                    size="large"
                />
            </Form.Item>
            <Form.Item
                hasFeedback
                label={t('newPasswordInputLabel')}
                name="new_password"
                rules={passwordValidations}
            >
                <Input
                    className={cls.control}
                    disabled={isLoading}
                    size="large"
                />
            </Form.Item>
            <Form.Item
                dependencies={['new_password']}
                hasFeedback
                label={t('repeatNewPasswordInputLabel')}
                name="repeatPassword"
                rules={[
                    {
                        required: true,
                        message: t('requiredControlError', {
                            controlName: t('passwordFieldNameError'),
                            ns: 'translation',
                        }),
                    },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            return !value ||
                                getFieldValue('new_password') === value
                                ? Promise.resolve()
                                : Promise.reject(
                                      new Error(
                                          t('passwordNotMatchedError', {
                                              ns: 'translation',
                                          }),
                                      ),
                                  );
                        },
                    }),
                ]}
            >
                <Input
                    className={cls.control}
                    disabled={isLoading}
                    size="large"
                />
            </Form.Item>
            <Form.Item
                className={cls.saveButton}
                shouldUpdate
            >
                {({ isFieldsTouched, getFieldsError }) => (
                    <Button
                        disabled={
                            !isFieldsTouched(true) ||
                            !!getFieldsError().filter(
                                ({ errors }) => errors.length,
                            ).length
                        }
                        htmlType="submit"
                        loading={isLoading}
                        type="primary"
                    >
                        {t('changePasswordButton')}
                    </Button>
                )}
            </Form.Item>
        </Form>
    );
});

ChangePasswordForm.displayName = 'ChangePasswordForm';
